import React from 'react'
import { CMultiSelect } from '@coreui/react-pro'

interface FilterMultiSelectProps {
  values: string[]
  onChange: (filterFn: (item: string) => boolean) => void
  /**
   * The column's current filter state, handed to a custom filter by CSmartTable as its third
   * argument. It is the seed string while the table's own `columnFilterValue` is in force, and
   * becomes the predicate this control supplied once the user picks something — so a string means
   * "show this selected", and anything else means the control is already holding its own state.
   */
  filterState?: unknown
  /**
   * The order to offer values in, as labels. Alphabetical where it is not given.
   *
   * Timeframes are the reason it exists: sorted as text they read "15m, 1D, 1H, 30m, 4H, 5m", which
   * is no order at all. The reference endpoint already returns them shortest-first, so a caller can
   * hand that straight in.
   */
  order?: string[]
}

const FilterMultiSelect: React.FC<FilterMultiSelectProps> = ({
  values,
  onChange,
  filterState,
  order,
}) => {
  const rank = (value: string) => {
    const index = order?.indexOf(value) ?? -1

    // Anything the order does not mention sorts after what it does, rather than to the front.
    return index === -1 ? Number.MAX_SAFE_INTEGER : index
  }

  // A column key that names a field the rows do not carry arrives here as undefined, and used to
  // take the page down on toLowerCase. A missing option is the better failure.
  const unique = [...new Set(values)].filter((value) => typeof value === 'string').sort((a, b) =>
    order ? rank(a) - rank(b) || a.localeCompare(b) : a.localeCompare(b),
  )
  const seeded = typeof filterState === 'string' ? filterState.toLowerCase() : undefined

  return (
    <CMultiSelect
      size="sm"
      onChange={(selected) => {
        const selectedValues = selected.map((element) => element.value)
        onChange((item) => {
          return Array.isArray(selectedValues) && selectedValues.length
            ? selectedValues.includes(item.toLowerCase())
            : true
        })
      }}
      options={unique.map((element) => {
        const value = element.toLowerCase()
        // Only ever adds `selected: true`. Passing `selected: false` on every render overrides the
        // component's own selection and breaks the filter entirely.
        return seeded === value
          ? { value, label: element, selected: true }
          : { value, label: element }
      })}
    />
  )
}

export default FilterMultiSelect
